import { Link } from "@tanstack/react-router";
import { CheckCircle2, MessageSquare, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useSetCurrentVersion, useVersions } from "@/hooks/use-versions";

interface VersionsDialogProps {
  slug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VersionsDialog({ slug, open, onOpenChange }: VersionsDialogProps) {
  const versions = useVersions(slug);
  const setCurrent = useSetCurrentVersion(slug);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base">Version history</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {versions.isLoading && <Skeleton className="h-32" />}
          {versions.isError && !versions.isLoading && (
            <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-center">
              <p className="text-sm text-destructive mb-3">Could not load version history.</p>
              <Button variant="outline" size="sm" onClick={() => versions.refetch()}>
                Try again
              </Button>
            </div>
          )}
          {!versions.isLoading && !versions.isError && versions.data?.length === 0 && (
            <div className="rounded-lg border-2 border-dashed border-border bg-muted/30 p-8 text-center">
              <p className="text-sm text-muted-foreground">No versions yet.</p>
            </div>
          )}
          {versions.data?.map((v) => (
            <Card key={v.id} className={v.isCurrent ? "border-primary/40 shadow-sm" : ""}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="text-xl w-12 shrink-0 text-center">v{v.versionNumber}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {v.label && <span className="font-medium text-sm">{v.label}</span>}
                    {v.isCurrent && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary flex items-center gap-1">
                        <Star className="h-3 w-3" />
                        current
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                    <span>{new Date(v.createdAt).toLocaleString()}</span>
                    <span>by {v.uploadedByName}</span>
                    {v.commentCount > 0 && (
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        {v.commentCount} comment{v.commentCount === 1 ? "" : "s"}
                        {v.unresolvedCount > 0 && (
                          <span className="text-warning ml-1">({v.unresolvedCount} open)</span>
                        )}
                      </span>
                    )}
                  </div>
                  {v.changeSummary && (
                    <p className="text-xs mt-1 text-muted-foreground border-l-2 border-border pl-2">
                      {v.changeSummary}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button variant="outline" size="sm" asChild onClick={() => onOpenChange(false)}>
                    <Link to="/solutions/$slug/v/$versionId" params={{ slug, versionId: v.id }}>
                      View
                    </Link>
                  </Button>
                  {!v.isCurrent && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrent.mutate(v.id)}
                      disabled={setCurrent.isPending}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                      Set current
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
